import { Button } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatusChip } from '@/components/ui/status-chip';
import { formatAmount, formatBrokerDateTime } from '@/features/brokers/batches/batch-formatters';
// Canonical status helpers from the batches feature — transactionStatusVariant is
// the superset the detail page renders through instead of its own copy.
import { batchStatusVariant, transactionStatusVariant } from '@/features/brokers/batches/batch-status';
import {
  type BrokerTransactionsQueryResult,
  useBrokerDeposits,
  useBrokerTransactionBatches,
  useBrokerTransfers,
  useBrokerWithdrawals,
} from '@/hooks/queries/brokers/use-broker-detail';
import type { WireBrokerTransaction, WireBrokerTransfer, WireTransactionBatch } from '@/services/api/brokers';
import { CHIP_XS_CLASS } from './broker-detail-format';
import { ErrorPanel, LoadMoreButton } from './detail-panels';

export const TXN_SEGMENT_VALUES = ['deposits', 'withdrawals', 'transfers', 'batches'] as const;
export type TxnSegment = (typeof TXN_SEGMENT_VALUES)[number];

/** `?txn=` deep-link values are validated against this exact set; anything else is ignored. */
export const isTxnSegment = (value: string | null): value is TxnSegment => value !== null && (TXN_SEGMENT_VALUES as readonly string[]).includes(value);

const SEGMENT_LABELS: Record<TxnSegment, string> = {
  deposits: 'Deposits',
  withdrawals: 'Withdrawals',
  transfers: 'Transfers',
  batches: 'Batches',
};

const batchRoute = (brokerId: string, batchId: string): string =>
  // batch_id can contain Dynamo composite-key separators — encode exactly once
  // when building the route
  `/operations/brokers/${brokerId}/batches/${encodeURIComponent(batchId)}`;

// Deposits/withdrawals share columns. Client Account ID stays PLAIN TEXT here —
// legacy parity, unlike the Clients tab link.
// Amount is a decimal STRING rendered verbatim — never Number()/format it.
const TRANSACTION_COLUMNS: ColumnDef<WireBrokerTransaction, unknown>[] = [
  { accessorKey: 'transaction_id', header: 'Transaction ID', cell: ({ row }) => <span className="font-mono text-xs">{row.original.transaction_id}</span> },
  { accessorKey: 'client_account_id', header: 'Client Account ID', cell: ({ row }) => <span className="font-mono text-xs">{row.original.client_account_id}</span> },
  { accessorKey: 'amount', header: 'Amount', cell: ({ row }) => <span className="tabular-nums">{row.original.amount}</span> },
  { accessorKey: 'currency', header: 'Currency' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusChip className={CHIP_XS_CLASS} variant={transactionStatusVariant(row.original.status)}>
        {row.original.status}
      </StatusChip>
    ),
  },
  { accessorKey: 'type', header: 'Type' },
  { accessorKey: 'created_at', header: 'Created', cell: ({ row }) => <DateCell format={formatBrokerDateTime} value={row.original.created_at} /> },
];

interface TransactionsTableProps {
  query: BrokerTransactionsQueryResult;
  emptyMessage: string;
  errorTitle: string;
}

const TransactionsTable = ({ query, emptyMessage, errorTitle }: TransactionsTableProps) => {
  // Full panel only when nothing loaded; a failed Load More keeps the table
  // mounted with its rows and surfaces the error inline below.
  if (query.isError && !query.data) return <ErrorPanel error={query.error} onRetry={() => query.refetch()} title={errorTitle} />;
  return (
    <div className="flex flex-col gap-3">
      <DataTable columns={TRANSACTION_COLUMNS} data={query.data} emptyMessage={emptyMessage} getRowId={(row) => row.transaction_id} isLoading={query.isPending} />
      {query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} title={errorTitle} />
      ) : (
        <LoadMoreButton hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} onLoadMore={() => query.fetchNextPage()} />
      )}
    </div>
  );
};
TransactionsTable.displayName = 'TransactionsTable';

const DepositsView = ({ brokerId }: { brokerId: string }) => {
  const query = useBrokerDeposits(brokerId);
  return <TransactionsTable emptyMessage="No deposits found." errorTitle="Failed to fetch deposits" query={query} />;
};
DepositsView.displayName = 'DepositsView';

const WithdrawalsView = ({ brokerId }: { brokerId: string }) => {
  const query = useBrokerWithdrawals(brokerId);
  return <TransactionsTable emptyMessage="No withdrawals found." errorTitle="Failed to fetch withdrawals" query={query} />;
};
WithdrawalsView.displayName = 'WithdrawalsView';

// Transfers use `timestamp`, not `created_at` — a shared "created" accessor would
// silently render blank cells here.
const TRANSFER_COLUMNS: ColumnDef<WireBrokerTransfer, unknown>[] = [
  { accessorKey: 'transfer_id', header: 'Transfer ID', cell: ({ row }) => <span className="font-mono text-xs">{row.original.transfer_id}</span> },
  { accessorKey: 'source_account_id', header: 'Source', cell: ({ row }) => <span className="font-mono text-xs">{row.original.source_account_id}</span> },
  { accessorKey: 'destination_account_id', header: 'Destination', cell: ({ row }) => <span className="font-mono text-xs">{row.original.destination_account_id}</span> },
  { accessorKey: 'amount', header: 'Amount', cell: ({ row }) => <span className="tabular-nums">{row.original.amount}</span> },
  { accessorKey: 'currency', header: 'Currency' },
  { accessorKey: 'transfer_type', header: 'Type' },
  { accessorKey: 'timestamp', header: 'Timestamp', cell: ({ row }) => <DateCell format={formatBrokerDateTime} value={row.original.timestamp} /> },
];

const TransfersView = ({ brokerId }: { brokerId: string }) => {
  const query = useBrokerTransfers(brokerId);
  if (query.isError && !query.data) return <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch transfers" />;
  return (
    <div className="flex flex-col gap-3">
      <DataTable columns={TRANSFER_COLUMNS} data={query.data} emptyMessage="No transfers found." getRowId={(row) => row.transfer_id} isLoading={query.isPending} />
      {query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch transfers" />
      ) : (
        <LoadMoreButton hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} onLoadMore={() => query.fetchNextPage()} />
      )}
    </div>
  );
};
TransfersView.displayName = 'TransfersView';

const BATCH_COLUMNS: ColumnDef<WireTransactionBatch, unknown>[] = [
  { accessorKey: 'batch_id', header: 'Batch ID', cell: ({ row }) => <span className="font-mono text-xs">{row.original.batch_id}</span> },
  { accessorKey: 'currency', header: 'Currency' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusChip className={CHIP_XS_CLASS} variant={batchStatusVariant(row.original.status)}>
        {row.original.status}
      </StatusChip>
    ),
  },
  { accessorKey: 'tx_count', header: 'Transactions', cell: ({ row }) => <span className="tabular-nums">{row.original.tx_count}</span> },
  {
    accessorKey: 'net_amount',
    header: 'Net Amount',
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatAmount(row.original.net_amount)} {row.original.currency}
      </span>
    ),
  },
  {
    accessorKey: 'batch_window_start',
    header: 'Window Start',
    cell: ({ row }) => <DateCell format={formatBrokerDateTime} value={row.original.batch_window_start} />,
  },
  { accessorKey: 'settled_at', header: 'Settled', cell: ({ row }) => <DateCell format={formatBrokerDateTime} value={row.original.settled_at} /> },
];

const SectionHeading = ({ children }: { children: string }) => <h3 className="text-[13px] font-medium uppercase tracking-wide text-muted-foreground">{children}</h3>;
SectionHeading.displayName = 'SectionHeading';

interface OpenBatchCardProps {
  batch: WireTransactionBatch;
  onOpen: () => void;
}

const OpenBatchCard = ({ batch, onOpen }: OpenBatchCardProps) => (
  <button
    className="flex flex-col gap-3 rounded-lg border border-l-[3px] border-l-blue-500 bg-card p-4 text-left transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    onClick={onOpen}
    type="button"
  >
    <span className="flex w-full items-center justify-between gap-2">
      <span className="text-lg font-bold">{batch.currency}</span>
      <StatusChip className={CHIP_XS_CLASS} variant={batchStatusVariant(batch.status)}>
        {batch.status}
      </StatusChip>
    </span>
    <span className="break-all font-mono text-xs text-muted-foreground">{batch.batch_id}</span>
    <span className="flex gap-6">
      <span className="flex flex-col gap-0.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Transactions</span>
        <span className="text-sm font-medium tabular-nums">{batch.tx_count}</span>
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Net Amount</span>
        <span className="text-sm font-medium tabular-nums">
          {formatAmount(batch.net_amount)} {batch.currency}
        </span>
      </span>
    </span>
    <span className="text-xs text-muted-foreground">Window closes {formatBrokerDateTime(batch.batch_window_end)}</span>
  </button>
);
OpenBatchCard.displayName = 'OpenBatchCard';

interface BatchPartition {
  openBatches: WireTransactionBatch[];
  previousBatches: WireTransactionBatch[];
}

const partitionBatches = (batches: WireTransactionBatch[]): BatchPartition => ({
  openBatches: batches.filter((batch) => batch.status === 'OPEN'),
  previousBatches: batches.filter((batch) => batch.status !== 'OPEN'),
});

const BatchesView = ({ brokerId }: { brokerId: string }) => {
  const navigate = useNavigate();
  const query = useBrokerTransactionBatches(brokerId);

  // The open/previous partition runs over EVERYTHING loaded so far and is
  // recomputed after each Load More. An OPEN batch beyond page 1 stays invisible
  // until loaded — accepted parity, do not eagerly fetch all pages.
  // Memoised so DataTable gets stable arrays.
  const batches = query.data;
  const { openBatches, previousBatches } = useMemo(() => partitionBatches(batches ?? []), [batches]);

  if (query.isPending) return <LoadingPanel />;
  // Full panel only when nothing loaded; a failed Load More keeps both sections
  // rendered and surfaces the error inline below them.
  if (query.isError && !batches) return <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch transaction batches" />;

  if (openBatches.length === 0 && previousBatches.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No transaction batches found.</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      {openBatches.length > 0 ? (
        <section className="flex flex-col gap-3">
          <SectionHeading>{openBatches.length === 1 ? 'Current open batch' : 'Current open batches'}</SectionHeading>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
            {openBatches.map((batch) => (
              <OpenBatchCard batch={batch} key={batch.batch_id} onOpen={() => navigate(batchRoute(brokerId, batch.batch_id))} />
            ))}
          </div>
        </section>
      ) : null}

      {previousBatches.length > 0 ? (
        <section className="flex flex-col gap-3">
          {/* Deliberate fix: legacy suppressed this
              heading when there were no open batches, leaving a bare table — always
              show it when the table renders */}
          <SectionHeading>Previous batches</SectionHeading>
          <DataTable
            columns={BATCH_COLUMNS}
            data={previousBatches}
            emptyMessage="No transaction batches found."
            getRowId={(row) => row.batch_id}
            onRowClick={(batch) => navigate(batchRoute(brokerId, batch.batch_id))}
          />
        </section>
      ) : null}

      {query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch transaction batches" />
      ) : (
        <LoadMoreButton hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} onLoadMore={() => query.fetchNextPage()} />
      )}
    </div>
  );
};
BatchesView.displayName = 'BatchesView';

interface TransactionsTabProps {
  brokerId: string;
  segment: TxnSegment;
  /** Owned by the page so the selection survives tab switches (legacy kept it alive) */
  onSegmentChange: (segment: TxnSegment) => void;
}

/**
 * 4-way sub-toggle. Only the active segment's
 * view mounts — first activation fires its query, and the cache keeps revisits warm.
 * The toggle is deliberately NOT URL-synced during use; only the initial `?txn=`
 * deep link is read, and the batch page's back-link is its only producer.
 */
export const TransactionsTab = ({ brokerId, segment, onSegmentChange }: TransactionsTabProps) => (
  <div className="flex flex-col gap-4">
    <fieldset className="flex flex-wrap gap-2">
      <legend className="sr-only">Transaction type</legend>
      {TXN_SEGMENT_VALUES.map((value) => (
        <Button aria-pressed={segment === value} key={value} onClick={() => onSegmentChange(value)} size="sm" type="button" variant={segment === value ? 'default' : 'outline'}>
          {SEGMENT_LABELS[value]}
        </Button>
      ))}
    </fieldset>

    {segment === 'deposits' ? <DepositsView brokerId={brokerId} /> : null}
    {segment === 'withdrawals' ? <WithdrawalsView brokerId={brokerId} /> : null}
    {segment === 'transfers' ? <TransfersView brokerId={brokerId} /> : null}
    {segment === 'batches' ? <BatchesView brokerId={brokerId} /> : null}
  </div>
);
TransactionsTab.displayName = 'TransactionsTab';
